import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Trophy, Plus, Trash2, Upload, Loader2, Crown } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";

export default function HonorRollAdminPage() {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    student_name: "", class_name: "", period: "Trimestre 1",
    rank: 1, average: 18, photo_url: "", comment: ""
  });
  const [uploading, setUploading] = useState(false);

  const load = () => {
    setLoading(true);
    api.get("/honor-roll").then(({ data }) => setEntries(data)).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) return toast.error("Image trop volumineuse (max 5 Mo)");
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/files/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm(f => ({ ...f, photo_url: data.url }));
      toast.success("Photo envoyée");
    } catch {
      toast.error("Erreur d'envoi");
    } finally { setUploading(false); }
  };

  const create = async () => {
    if (!form.student_name || !form.class_name) return toast.error("Nom et classe requis");
    setSubmitting(true);
    try {
      await api.post("/honor-roll", { ...form, rank: parseInt(form.rank), average: parseFloat(form.average) });
      toast.success("Entrée ajoutée au tableau d'honneur");
      setOpen(false);
      setForm({ student_name: "", class_name: "", period: "Trimestre 1", rank: 1, average: 18, photo_url: "", comment: "" });
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    } finally { setSubmitting(false); }
  };

  const remove = async (entry) => {
    if (!window.confirm(`Supprimer ${entry.student_name} du tableau d'honneur ?`)) return;
    try {
      await api.delete(`/honor-roll/${entry.id}`);
      toast.success("Entrée supprimée");
      load();
    } catch { toast.error("Erreur"); }
  };

  return (
    <div className="space-y-5" data-testid="page-honor-roll-admin">
      <div className="flex items-end justify-between gap-3 flex-wrap">
        <div>
          <div className="chip mb-2"><Trophy className="h-3 w-3" /><span>Excellence académique</span></div>
          <h2 className="font-heading text-3xl font-extrabold text-[#1E3A8A] tracking-tight">Tableau d&apos;honneur</h2>
          <p className="text-sm text-slate-500 mt-1">{entries.length} élève(s) à l&apos;honneur · visible publiquement sur <code>/tableau-honneur</code></p>
        </div>
        <Button onClick={() => setOpen(true)} className="bg-[#FBBF24] hover:bg-yellow-500 text-[#0F172A] gap-2" data-testid="add-honor-btn">
          <Plus className="h-4 w-4" /> Ajouter un élève
        </Button>
      </div>

      {loading ? (
        <div className="text-center py-12"><Loader2 className="h-6 w-6 animate-spin mx-auto text-slate-400" /></div>
      ) : entries.length === 0 ? (
        <div className="bg-white border border-slate-200 rounded-xl p-10 text-center text-slate-500">
          <Trophy className="h-10 w-10 mx-auto mb-3 opacity-30" />
          Aucun élève à l&apos;honneur pour le moment. Cliquez sur &quot;Ajouter un élève&quot;.
        </div>
      ) : (
        <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
          <table className="w-full">
            <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-500">
              <tr>
                <th className="px-5 py-3 text-left">Photo</th>
                <th className="px-5 py-3 text-left">Élève</th>
                <th className="px-5 py-3 text-left">Classe</th>
                <th className="px-5 py-3 text-left">Période</th>
                <th className="px-5 py-3 text-center">Rang</th>
                <th className="px-5 py-3 text-right">Moyenne</th>
                <th className="px-5 py-3 text-right">Action</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {entries.map(e => (
                <tr key={e.id} className="hover:bg-slate-50" data-testid={`hr-row-${e.id}`}>
                  <td className="px-5 py-3">
                    {e.photo_url ? (
                      <img src={e.photo_url} alt={e.student_name} className="w-12 h-12 rounded-full object-cover" />
                    ) : (
                      <div className="w-12 h-12 rounded-full bg-gradient-to-br from-[#1E3A8A] to-[#166534] flex items-center justify-center text-white font-bold text-xs">
                        {e.student_name.split(" ").map(n => n[0]).slice(0, 2).join("")}
                      </div>
                    )}
                  </td>
                  <td className="px-5 py-3 text-sm font-semibold text-[#0F172A]">{e.student_name}</td>
                  <td className="px-5 py-3 text-sm text-slate-600">{e.class_name}</td>
                  <td className="px-5 py-3 text-xs text-slate-500">{e.period}</td>
                  <td className="px-5 py-3 text-center">
                    <span className={`inline-flex items-center gap-1 text-xs font-bold px-2 py-1 rounded-full ${e.rank === 1 ? "bg-[#FBBF24] text-[#0F172A]" : e.rank === 2 ? "bg-slate-200 text-slate-700" : e.rank === 3 ? "bg-orange-100 text-orange-700" : "bg-blue-100 text-blue-700"}`}>
                      {e.rank === 1 && <Crown className="h-3 w-3" />} #{e.rank}
                    </span>
                  </td>
                  <td className="px-5 py-3 text-sm text-right font-bold text-[#166534]">{e.average.toFixed(2)}/20</td>
                  <td className="px-5 py-3 text-right">
                    <Button size="sm" variant="outline" onClick={() => remove(e)} className="border-red-300 text-red-700 hover:bg-red-50 text-xs" data-testid={`hr-delete-${e.id}`}>
                      <Trash2 className="h-3 w-3" />
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="hr-create-dialog">
          <DialogHeader><DialogTitle>Ajouter un élève au tableau d&apos;honneur</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Nom de l&apos;élève *</Label>
                <Input value={form.student_name} onChange={(e) => setForm({ ...form, student_name: e.target.value })} placeholder="Mariama Diallo" data-testid="hr-name" />
              </div>
              <div>
                <Label>Classe *</Label>
                <Input value={form.class_name} onChange={(e) => setForm({ ...form, class_name: e.target.value })} placeholder="Primaire CE2" data-testid="hr-class" />
              </div>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <Label>Période</Label>
                <Select value={form.period} onValueChange={(v) => setForm({ ...form, period: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Trimestre 1">Trimestre 1</SelectItem>
                    <SelectItem value="Trimestre 2">Trimestre 2</SelectItem>
                    <SelectItem value="Trimestre 3">Trimestre 3</SelectItem>
                    <SelectItem value="Année complète">Année complète</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Rang</Label>
                <Input type="number" min="1" value={form.rank} onChange={(e) => setForm({ ...form, rank: e.target.value })} data-testid="hr-rank" />
              </div>
              <div>
                <Label>Moyenne /20</Label>
                <Input type="number" min="0" max="20" step="0.01" value={form.average} onChange={(e) => setForm({ ...form, average: e.target.value })} data-testid="hr-avg" />
              </div>
            </div>
            <div>
              <Label>Photo de l&apos;élève (optionnelle)</Label>
              <div className="flex items-center gap-2 mt-1">
                <input id="hr-file" type="file" accept="image/*" onChange={upload} className="hidden" data-testid="hr-file" />
                <label htmlFor="hr-file" className="cursor-pointer text-xs px-3 py-2 rounded-md bg-slate-100 hover:bg-slate-200 flex items-center gap-1">
                  {uploading ? <Loader2 className="h-3 w-3 animate-spin" /> : <Upload className="h-3 w-3" />}
                  {form.photo_url ? "Remplacer la photo" : "Téléverser une photo"}
                </label>
                {form.photo_url && <img src={form.photo_url} alt="" className="w-12 h-12 rounded-full object-cover" />}
              </div>
            </div>
            <div>
              <Label>Commentaire (optionnel)</Label>
              <Input value={form.comment} onChange={(e) => setForm({ ...form, comment: e.target.value })} placeholder="Élève brillante et engagée" data-testid="hr-comment" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Annuler</Button>
            <Button onClick={create} disabled={submitting} className="bg-[#FBBF24] hover:bg-yellow-500 text-[#0F172A] font-bold" data-testid="hr-confirm-create">
              {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Trophy className="h-4 w-4 mr-1" /> Ajouter</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
